import { useState, useEffect } from 'react';
import assignmentNotificationService from '../services/assignmentNotificationService';

const NewLeadModalNotification = ({ employeeId }) => {
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!employeeId) return;

    const checkForNewNotification = () => {
      const unreadNotifications = assignmentNotificationService.getUnreadNotifications(employeeId);
      
      if (unreadNotifications.length > 0 && !showModal) {
        const latestNotification = unreadNotifications[0];
        setNotification(latestNotification);
        setShowModal(true);
        setIsAnimating(true);
        
        // Play sound (optional)
        playNotificationSound();
      }
    };

    // Check immediately
    checkForNewNotification();

    // Check every 2 seconds
    const interval = setInterval(checkForNewNotification, 2000);

    // Listen for custom events
    const handleNewAssignment = () => {
      console.log('🎯 Modal: New assignment detected');
      setTimeout(checkForNewNotification, 100);
    };

    window.addEventListener('newAssignmentNotification', handleNewAssignment);

    return () => {
      clearInterval(interval);
      window.removeEventListener('newAssignmentNotification', handleNewAssignment);
    };
  }, [employeeId, showModal]);

  const playNotificationSound = () => {
    // Create a simple notification sound
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Could not play sound:', error);
    }
  };

  const handleAccept = () => {
    if (notification) {
      assignmentNotificationService.markAsRead(notification.id);
    }
    setIsAnimating(false);
    setTimeout(() => {
      setShowModal(false);
      setNotification(null);
    }, 300);
  };

  const handleViewLater = () => {
    // Don't mark as read, just close modal
    setIsAnimating(false);
    setTimeout(() => {
      setShowModal(false);
      setNotification(null);
    }, 300);
  };

  if (!showModal || !notification) return null;

  return (
    <>
      <style>{`
        @keyframes modalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideUp {
          from {
            transform: translateY(100px) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes modalSlideDown {
          from {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          to {
            transform: translateY(100px) scale(0.9);
            opacity: 0;
          }
        }

        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes shine {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }

        .modal-overlay {
          animation: modalFadeIn 0.3s ease-out;
        }

        .modal-content {
          animation: ${isAnimating ? 'modalSlideUp' : 'modalSlideDown'} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti 3s ease-out forwards;
        }

        .shine-effect {
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          background-size: 200% 100%;
          animation: shine 2s infinite;
        }
      `}</style>

      {/* Overlay */}
      <div className="modal-overlay fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        
        {/* Confetti Effect */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="confetti-piece"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-20px',
              backgroundColor: ['#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'][Math.floor(Math.random() * 5)],
              animationDelay: `${Math.random() * 0.5}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}

        {/* Modal Content */}
        <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden relative">
          
          {/* Header with Gradient */}
          <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-8 text-white overflow-hidden">
            <div className="shine-effect absolute inset-0"></div>
            
            {/* Icon */}
            <div className="relative flex justify-center mb-4">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform">
                <span className="text-6xl animate-bounce">🎯</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="relative text-3xl font-bold text-center mb-2">
              New Lead Assigned!
            </h2>
            <p className="relative text-center text-teal-100 text-sm">
              You have a new customer to assist
            </p>
          </div>

          {/* Body */}
          <div className="p-8">
            {/* Lead Details Card */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 mb-6 border-2 border-teal-200 shadow-inner">
              
              {/* Customer Name */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-teal-200">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-2xl">👤</span>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Customer Name</p>
                  <p className="text-lg font-bold text-gray-900">
                    {notification.leadName}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📧</span>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 font-medium">Email Address</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {notification.leadEmail}
                  </p>
                </div>
              </div>

              {/* Service Type */}
              {notification.serviceType && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🎫</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 font-medium">Service Type</p>
                    <span className="inline-block mt-1 px-3 py-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-xs font-bold rounded-full shadow">
                      {notification.serviceType.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Timestamp */}
            <div className="flex items-center justify-center gap-2 mb-6 text-gray-500 text-sm">
              <span>🕐</span>
              <span>
                Assigned at {new Date(notification.assignedAt).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleViewLater}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all transform hover:scale-105 active:scale-95"
              >
                View Later
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <span>✓</span>
                <span>Got it!</span>
              </button>
            </div>

            {/* Info Text */}
            <p className="text-center text-xs text-gray-500 mt-4">
              The lead will appear in your dashboard automatically
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewLeadModalNotification;
